// Design tokens from the v2 prototype. Person colours are keyed by member colour key
// (amber/rose/sky/green/violet) plus `pFamily` for whole-family plans.

export type ThemeKey = 'light' | 'dark' | 'colorful' | 'glass' | 'multi' | 'ink' | FamilyThemeKey;
export type FamilyBackgroundKey = 'peach' | 'sage' | 'sky' | 'night';
type FamilyThemeKey = 'family_peach' | 'family_sage' | 'family_sky' | 'family_night';
export type Tokens = Record<string, string>;

export const THEME_OPTIONS: { key: ThemeKey; label: string }[] = [
  { key: 'light', label: 'Light' },
  { key: 'dark', label: 'Dark' },
  { key: 'colorful', label: 'Sky' },
  { key: 'glass', label: 'Glass' },
  { key: 'multi', label: 'Multi' },
  { key: 'ink', label: 'E-ink' },
];

// ---- "Our family" scene backgrounds -------------------------------------------------------
// Warm paper-like palettes with serif headings, used only by the family scene (picked in its
// customizer), so they are not in THEME_OPTIONS. Extra tokens: headFont/clockFont (serif type),
// accentInk (the next prayer), salatArt (illustration behind the prayer card), leaf, bodyBg.

/** Mosque, palms and a low sun, as a single-colour SVG data URI for a card background. */
function salatArt(color: string): string {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 200' preserveAspectRatio='xMidYMax meet'>` +
    `<g fill='${color}'>` +
    `<circle cx='245' cy='92' r='26' opacity='0.45'/>` +
    `<path opacity='0.55' d='M0 200V150c40-14 80-18 120-10s90 6 140-6 100-10 140 4V200Z'/>` +
    // Mosque: central dome, side domes, two minarets.
    `<path d='M178 200v-58h12c2-20 16-34 32-38 16 4 30 18 32 38h12v58Z'/>` +
    `<path d='M222 96c2 0 3 2 3 4v6h-6v-6c0-2 1-4 3-4Z'/>` +
    `<path d='M150 200v-34h8c1-10 7-16 14-18 7 2 13 8 14 18h8v34Z'/>` +
    `<path d='M256 200v-34h8c1-10 7-16 14-18 7 2 13 8 14 18h8v34Z'/>` +
    `<path d='M132 200V96l5-14 5 14v104Z'/><path d='M302 200V84l5-14 5 14v116Z'/>` +
    `<rect x='129' y='108' width='16' height='4' rx='2'/><rect x='299' y='98' width='16' height='4' rx='2'/>` +
    // Palms.
    `<path d='M48 200c2-30 6-56 14-80l4 1c-7 24-10 50-11 79Z'/>` +
    `<path d='M64 120c-14-10-32-10-46-2 14-2 30 0 42 8-16-2-30 4-40 14 14-8 30-10 44-6-10 4-18 12-22 22 10-10 22-16 34-16 8 2 16 10 20 22 0-14-6-26-16-34 12 0 24 6 32 16-4-12-16-22-30-24 14-6 30-6 42 2-10-12-28-16-44-10Z'/>` +
    `<path d='M356 200c-1-24-4-44-10-62l4-1c6 18 10 39 11 63Z'/>` +
    `<path d='M348 138c-11-8-26-8-37-1 11-1 24 0 34 6-13-1-24 3-32 11 11-6 24-8 35-5-8 3-14 10-17 18 8-8 17-13 27-13 7 2 13 8 16 18 0-11-5-21-13-27 10 0 19 5 26 13-3-10-13-18-24-19 11-5 24-5 34 2-8-10-22-13-35-8Z'/>` +
    `</g></svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
}

const SERIF = "'Newsreader', Georgia, 'Times New Roman', serif";

function familyPalette(p: Tokens & { cream: string; salat: string; weather: string; artColor: string }): Tokens {
  const { cream, salat, weather, artColor, ...rest } = p;
  const tones: Tokens = {};
  for (let i = 1; i <= 10; i++) tones[`c${i}`] = cream;
  return {
    ...tones,
    c2: weather,
    c8: salat,
    card: cream,
    cardSolid: cream,
    sheet: cream,
    cloudFill: '#FFFFFF',
    softBd: 'transparent',
    toggleRing: 'transparent',
    knobOff: '#FFFFFF',
    field: '#FFFFFF',
    seg: '#FFFFFF',
    blur: 'none',
    headFont: SERIF,
    clockFont: SERIF,
    salatArt: salatArt(artColor),
    ...rest,
  };
}

export const FAMILY_BACKGROUNDS: { key: FamilyBackgroundKey; label: string; swatch: string }[] = [
  { key: 'peach', label: 'Peach', swatch: 'linear-gradient(135deg,#FCF3EA,#F6DFCF)' },
  { key: 'sage', label: 'Sage', swatch: 'linear-gradient(135deg,#F3F6EE,#DCE6CF)' },
  { key: 'sky', label: 'Sky', swatch: 'linear-gradient(135deg,#F1F6FB,#D6E5F3)' },
  { key: 'night', label: 'Night', swatch: 'linear-gradient(135deg,#2A231E,#14110F)' },
];

export function familyTheme(bg: string | null | undefined): ThemeKey {
  return bg === 'sage' || bg === 'sky' || bg === 'night' ? (`family_${bg}` as ThemeKey) : 'family_peach';
}

const WARM_PEOPLE = { pAmber: '#EDA36B', pRose: '#E48C8C', pSky: '#8DB4DA', pGreen: '#7FB28F', pViolet: '#A994D6', pFamily: '#8DB27F' };

export const THEMES: Record<ThemeKey, Tokens> = {
  family_peach: familyPalette({
    scheme: 'light', bg: 'linear-gradient(160deg,#FCF6F0 0%,#F9EEE5 55%,#F6E7DC 100%)', bodyBg: '#F9EEE5',
    cream: '#FFFBF7', salat: 'linear-gradient(175deg,#FFF8F2 0%,#FCE6D8 100%)', weather: 'linear-gradient(175deg,#F5F9FD 0%,#DDEBF7 100%)',
    artColor: '#EDBFA3', line: '#F0E1D5', line2: '#F2E6DC', ink: '#3A2219', muted: '#8C7064', muted2: '#6B5045', soft: '#FCE4D6',
    bd: '#E6D2C4', track: '#F1E3D8', onInk: '#FFF8F2', onInkSub: '#E9CDBD', toggleOff: '#E8D8CC', goodBg: '#E4EEDD', goodFg: '#3F6338',
    green: '#6E9A6A', rain: '#5B86B3', now: '#C2553A', sun: '#F2A93B', sunFill: '#F8C04E', barFill: 'linear-gradient(90deg,#9CC48F,#F2C46B,#E8875E)',
    chartLine: '#C2553A', chartFillA: 'rgba(194,85,58,0.18)', chartFillB: 'rgba(194,85,58,0)', cardShadow: '0 6px 22px rgba(120,70,40,0.07)',
    accentInk: '#A8432A', leaf: '#AEB98F', ...WARM_PEOPLE,
  }),
  family_sage: familyPalette({
    scheme: 'light', bg: 'linear-gradient(160deg,#F5F7F0 0%,#EDF1E6 55%,#E6ECDD 100%)', bodyBg: '#EDF1E6',
    cream: '#FBFCF8', salat: 'linear-gradient(175deg,#F8FBF3 0%,#E2ECD5 100%)', weather: 'linear-gradient(175deg,#F5F9FD 0%,#DDEBF7 100%)',
    artColor: '#BCCDA9', line: '#E3E9DA', line2: '#E8EDE1', ink: '#24301F', muted: '#6E7966', muted2: '#4F5A48', soft: '#E4ECDA',
    bd: '#D3DCC8', track: '#E4EADB', onInk: '#F8FAF4', onInkSub: '#C9D3BE', toggleOff: '#D8E0CD', goodBg: '#DDE9D3', goodFg: '#355B2E',
    green: '#5E8A55', rain: '#4F7EA8', now: '#B4532F', sun: '#E2A33A', sunFill: '#F1C25A', barFill: 'linear-gradient(90deg,#8FBF84,#E9C46A,#E08A5E)',
    chartLine: '#4E7344', chartFillA: 'rgba(78,115,68,0.18)', chartFillB: 'rgba(78,115,68,0)', cardShadow: '0 6px 22px rgba(60,80,40,0.07)',
    accentInk: '#4E7344', leaf: '#8FA67A', ...WARM_PEOPLE,
  }),
  family_sky: familyPalette({
    scheme: 'light', bg: 'linear-gradient(160deg,#F3F7FB 0%,#E9F1F8 55%,#E1EBF5 100%)', bodyBg: '#E9F1F8',
    cream: '#FBFDFF', salat: 'linear-gradient(175deg,#F6FAFD 0%,#DCE8F5 100%)', weather: 'linear-gradient(175deg,#FFF9F3 0%,#F7E7D6 100%)',
    artColor: '#B3C9E0', line: '#E0E9F2', line2: '#E6EEF5', ink: '#1E2B3A', muted: '#66778A', muted2: '#4A5A6C', soft: '#E0EBF6',
    bd: '#CCDAE8', track: '#E1EAF3', onInk: '#F6FAFE', onInkSub: '#C3D3E3', toggleOff: '#D3DFEB', goodBg: '#DDEBDD', goodFg: '#355B3A',
    green: '#5F8F68', rain: '#3D6FA3', now: '#C2553A', sun: '#EDA63A', sunFill: '#F6C25A', barFill: 'linear-gradient(90deg,#8FC0E0,#F2C46B,#E8875E)',
    chartLine: '#2F5F93', chartFillA: 'rgba(47,95,147,0.16)', chartFillB: 'rgba(47,95,147,0)', cardShadow: '0 6px 22px rgba(40,70,110,0.07)',
    accentInk: '#2F5F93', leaf: '#9FB59A', ...WARM_PEOPLE,
  }),
  family_night: familyPalette({
    scheme: 'dark', bg: 'linear-gradient(160deg,#241D18 0%,#1B1613 60%,#14110F 100%)', bodyBg: '#1B1613',
    cream: '#2A231E', salat: 'linear-gradient(175deg,#2E251F 0%,#3B2A20 100%)', weather: 'linear-gradient(175deg,#26303A 0%,#1E2832 100%)',
    artColor: '#5C4535', line: '#3A302A', line2: '#342B25', ink: '#F5EADF', muted: '#B9A699', muted2: '#D6C5B8', soft: '#3A2F28',
    bd: '#4A3E36', track: '#3A302A', onInk: '#211B17', onInkSub: '#6B5A4E', field: '#342B25', seg: '#4A3E36', toggleOff: '#4A3E36',
    knobOff: '#B9A699', goodBg: '#2E3A2A', goodFg: '#A9C99E', green: '#93BE8E', rain: '#8FB0D0', now: '#E8906B', sun: '#E8C46A',
    sunFill: '#E8C46A', barFill: 'linear-gradient(90deg,#93BE8E,#E8C46A,#E8906B)', chartLine: '#E8906B', chartFillA: 'rgba(232,144,107,0.22)',
    chartFillB: 'rgba(232,144,107,0)', cardShadow: 'none', cloudFill: '#2A231E', accentInk: '#F0A27F', leaf: '#6F7D5C',
    pAmber: '#E0A86E', pRose: '#D892AC', pSky: '#93B2D4', pGreen: '#86C2AE', pViolet: '#B3A3DE', pFamily: '#93BE8E',
  }),
  light: {
    scheme: 'light', bg: '#F2F2ED', card: '#FBFBF8', cardSolid: '#FBFBF8', sheet: '#FBFBF8', cloudFill: '#FBFBF8',
    line: '#E4E4DD', line2: '#ECECE5', ink: '#232521', muted: '#6C6D66', muted2: '#55574F', soft: '#EBECE5', softBd: 'transparent',
    bd: '#D6D6CF', track: '#E8E8E1', onInk: '#F7F7F2', onInkSub: '#C9CAC2', field: '#FFFFFF', seg: '#FFFFFF',
    toggleOff: '#DADAD3', toggleRing: 'transparent', knobOff: '#FFFFFF', goodBg: '#E3EADD', goodFg: '#3D5A37', green: '#5E7F57',
    rain: '#4E6E8E', now: '#B4532F', sun: '#232521', sunFill: 'transparent', barFill: '#232521', chartLine: '#232521',
    chartFillA: 'rgba(35,37,33,0.14)', chartFillB: 'rgba(35,37,33,0)', blur: 'none', cardShadow: 'none',
    pAmber: '#C98B4E', pRose: '#B8728C', pSky: '#6E8DB0', pGreen: '#5E9A86', pViolet: '#8A78B8', pFamily: '#6E9A6A',
  },
  dark: {
    scheme: 'dark', bg: '#161715', card: '#1F201D', cardSolid: '#1F201D', sheet: '#1F201D', cloudFill: '#1F201D',
    line: '#2E2F2B', line2: '#292A26', ink: '#ECECE5', muted: '#9A9B93', muted2: '#B5B6AE', soft: '#262723', softBd: 'transparent',
    bd: '#3B3C37', track: '#33342F', onInk: '#161715', onInkSub: '#4A4B45', field: '#262723', seg: '#3B3C37',
    toggleOff: '#3B3C37', toggleRing: 'transparent', knobOff: '#9A9B93', goodBg: '#2C3829', goodFg: '#A8C79E', green: '#8DB384',
    rain: '#8FB0D0', now: '#E08A63', sun: '#E8C46A', sunFill: 'transparent', barFill: 'linear-gradient(90deg,#93BE8E,#E8C46A)',
    chartLine: '#E8C46A', chartFillA: 'rgba(232,196,106,0.25)', chartFillB: 'rgba(232,196,106,0)', blur: 'none', cardShadow: 'none',
    pAmber: '#E0A86E', pRose: '#D892AC', pSky: '#93B2D4', pGreen: '#86C2AE', pViolet: '#B3A3DE', pFamily: '#93BE8E',
  },
  colorful: {
    scheme: 'dark', bg: 'linear-gradient(155deg,#16408F 0%,#2F6CC0 40%,#4C93D3 68%,#2A8C8C 100%)',
    card: 'rgba(255,255,255,0.13)', cardSolid: '#2F67B4', sheet: 'rgba(40,86,160,0.92)', cloudFill: '#FFFFFF',
    line: 'rgba(255,255,255,0.2)', line2: 'rgba(255,255,255,0.14)', ink: '#FFFFFF', muted: 'rgba(255,255,255,0.86)',
    muted2: 'rgba(255,255,255,0.92)', soft: 'rgba(255,255,255,0.16)', softBd: 'transparent', bd: 'rgba(255,255,255,0.34)',
    track: 'rgba(255,255,255,0.2)', onInk: '#163E80', onInkSub: '#41659C', field: 'rgba(255,255,255,0.18)',
    seg: 'rgba(255,255,255,0.34)', toggleOff: 'rgba(255,255,255,0.25)', toggleRing: 'transparent', knobOff: '#FFFFFF',
    goodBg: '#A6EB6C', goodFg: '#173E1A', green: '#B6F07A', rain: '#8FE9FF', now: '#FFB547', sun: '#FFD23F', sunFill: '#FFD23F',
    barFill: 'linear-gradient(90deg,#8FE36A,#FFC93C,#FF8A3D)', chartLine: '#FFD23F', chartFillA: 'rgba(255,210,63,0.5)',
    chartFillB: 'rgba(143,233,255,0.04)', blur: 'blur(18px) saturate(140%)', cardShadow: '0 10px 30px rgba(0,25,70,0.18)',
    pAmber: '#FF9F43', pRose: '#FF6FA8', pSky: '#5AC8FA', pGreen: '#4FE3C1', pViolet: '#C9A2FF', pFamily: '#7BE07B',
  },
  glass: {
    scheme: 'light', bg: 'linear-gradient(140deg,#E6D9F7 0%,#CFE2F8 42%,#DDF0EA 70%,#F8E3D4 100%)',
    card: 'rgba(255,255,255,0.46)', cardSolid: '#F1F4FA', sheet: 'rgba(255,255,255,0.94)', cloudFill: '#FFFFFF',
    line: 'rgba(255,255,255,0.75)', line2: 'rgba(30,38,60,0.08)', ink: '#1C2333', muted: '#4E5668', muted2: '#3A4254',
    soft: 'rgba(255,255,255,0.5)', softBd: 'transparent', bd: 'rgba(30,38,60,0.16)', track: 'rgba(30,38,60,0.1)', onInk: '#FFFFFF',
    onInkSub: 'rgba(255,255,255,0.75)', field: 'rgba(255,255,255,0.7)', seg: '#FFFFFF', toggleOff: 'rgba(30,38,60,0.14)',
    toggleRing: 'transparent', knobOff: '#FFFFFF', goodBg: 'rgba(120,200,140,0.3)', goodFg: '#21593A', green: '#2F8A57',
    rain: '#3A79C9', now: '#E0623A', sun: '#E8A317', sunFill: '#FFD55A', barFill: 'linear-gradient(90deg,#7FC8F8,#B79CF2,#F6A57A)',
    chartLine: '#7A63D9', chartFillA: 'rgba(122,99,217,0.28)', chartFillB: 'rgba(122,99,217,0)', blur: 'blur(24px) saturate(170%)',
    cardShadow: '0 10px 30px rgba(70,80,130,0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
    pAmber: '#F08A3C', pRose: '#E2588F', pSky: '#3E95E6', pGreen: '#2FA58A', pViolet: '#8B6FE0', pFamily: '#3FA66B',
  },
  multi: {
    scheme: 'dark', bg: 'radial-gradient(circle at 12% 8%,#242A60 0%,#0D0F1E 60%)', card: '#1A1D33', cardSolid: '#1A1D33',
    sheet: '#20244A', cloudFill: '#FFFFFF', line: 'rgba(255,255,255,0.12)', line2: 'rgba(255,255,255,0.1)', ink: '#FFFFFF',
    muted: 'rgba(255,255,255,0.8)', muted2: 'rgba(255,255,255,0.9)', soft: 'rgba(255,255,255,0.08)', softBd: 'transparent',
    bd: 'rgba(255,255,255,0.25)', track: 'rgba(255,255,255,0.2)', onInk: '#14173A', onInkSub: '#4A4F80',
    field: 'rgba(255,255,255,0.18)', seg: 'rgba(255,255,255,0.34)', toggleOff: 'rgba(255,255,255,0.25)', toggleRing: 'transparent',
    knobOff: '#FFFFFF', goodBg: '#A6EB6C', goodFg: '#173E1A', green: '#B6F07A', rain: '#8FE9FF', now: '#FFB547', sun: '#FFD23F',
    sunFill: '#FFD23F', barFill: 'linear-gradient(90deg,#8FE36A,#FFC93C,#FF8A3D)', chartLine: '#FFD23F',
    chartFillA: 'rgba(255,210,63,0.4)', chartFillB: 'rgba(255,210,63,0)', blur: 'none', cardShadow: '0 10px 28px rgba(0,0,0,0.35)',
    pAmber: '#FF9F43', pRose: '#FF6FA8', pSky: '#5AC8FA', pGreen: '#4FE3C1', pViolet: '#C9A2FF', pFamily: '#7BE07B',
    c1: 'linear-gradient(150deg,#4A3A8C,#2A2560)', c2: 'linear-gradient(150deg,#2F6FD6,#1D4A9E)',
    c3: 'linear-gradient(150deg,#147A78,#0E5357)', c4: 'linear-gradient(150deg,#B5541E,#7A3416)',
    c5: 'linear-gradient(150deg,#2B2F6E,#1C1F4A)', c6: 'linear-gradient(150deg,#A3324E,#6E1F38)',
    c7: 'linear-gradient(150deg,#2E7D4F,#1D5536)', c8: 'linear-gradient(150deg,#4A3A8C,#2A2560)',
    c9: 'linear-gradient(150deg,#2F6FD6,#1D4A9E)', c10: 'linear-gradient(150deg,#147A78,#0E5357)',
    scmorning: 'linear-gradient(150deg,#B5541E,#7A3416)', scevening: 'linear-gradient(150deg,#2B2F6E,#1C1F4A)',
    scoutdoor: 'linear-gradient(150deg,#2E7D4F,#1D5536)', scfocus: 'linear-gradient(150deg,#A3324E,#6E1F38)',
  },
  ink: {
    scheme: 'light', bg: '#FFFFFF', card: '#FFFFFF', cardSolid: '#FFFFFF', sheet: '#FFFFFF', cloudFill: '#FFFFFF',
    line: '#000000', line2: '#000000', ink: '#000000', muted: '#000000', muted2: '#000000', soft: '#FFFFFF', softBd: '#000000',
    bd: '#000000', track: '#FFFFFF', onInk: '#FFFFFF', onInkSub: '#FFFFFF', field: '#FFFFFF', seg: '#000000',
    toggleOff: '#FFFFFF', toggleRing: '#000000', knobOff: '#000000', goodBg: '#000000', goodFg: '#FFFFFF', green: '#000000',
    rain: '#000000', now: '#000000', sun: '#000000', sunFill: 'transparent', barFill: '#000000', chartLine: '#000000',
    chartFillA: 'rgba(0,0,0,0)', chartFillB: 'rgba(0,0,0,0)', blur: 'none', cardShadow: 'none', segFg: '#FFFFFF',
    pAmber: '#000000', pRose: '#000000', pSky: '#000000', pGreen: '#000000', pViolet: '#000000', pFamily: '#000000',
  },
};

const ALL_KEYS: string[] = (() => {
  const seen: Record<string, true> = {};
  for (const k of Object.keys(THEMES) as ThemeKey[]) for (const t of Object.keys(THEMES[k])) seen[t] = true;
  return Object.keys(seen);
})();

export function resolveTokens(theme: ThemeKey, reduceEffects: boolean): Tokens {
  const t: Tokens = { ...(THEMES[theme] || THEMES.light) };
  if (reduceEffects) {
    t.card = t.cardSolid!;
    t.blur = 'none';
  }
  return t;
}

/** Writes tokens as CSS custom properties, removing ones the theme doesn't define. */
export function applyTheme(el: HTMLElement, theme: ThemeKey, reduceEffects: boolean): void {
  const t = resolveTokens(theme, reduceEffects);
  for (const k of ALL_KEYS) {
    const v = t[k];
    if (v !== undefined) el.style.setProperty(`--${k}`, v);
    else el.style.removeProperty(`--${k}`);
  }
  el.style.colorScheme = t.scheme!;
  el.setAttribute('data-theme', theme);
}

export type ColorKey = 'amber' | 'rose' | 'sky' | 'green' | 'violet';

/** CSS colour for a member's colour key, or the family colour when null. */
export function personColor(colorKey: string | null | undefined): string {
  if (!colorKey) return 'var(--pFamily)';
  return `var(--p${colorKey.charAt(0).toUpperCase()}${colorKey.slice(1)}, var(--bd))`;
}
